// fileText.js — turn an uploaded intake/review document into PLAIN TEXT for extraction.
//
// The desk's division of labor keeps parsing in the browser and only TEXT crosses to the server
// (the model never sees bytes it can't read, and edge functions stay lean). Four formats:
//
//   .txt/.md  — read as-is
//   .docx     — a zip; we inflate word/document.xml with the browser's native DecompressionStream
//               (same hand-rolled spirit as zipStore.js, which writes zips) and strip to text
//   .xlsx/.xls — SheetJS (already a dependency for the materials import); all sheets, row per line
//   .pdf      — pdf.js, loaded lazily so the main bundle never carries it
//
// The honest limit, enforced here: a file whose extracted text is effectively empty (a scanned
// PDF, an image-only page) is REFUSED with 'file-no-text' — the desk hard-blocks rather than
// letting a model hallucinate an intake out of nothing. OCR/vision is a logged phase-7 candidate,
// not a silent fallback.

const dosName = (bytes, off, len) => new TextDecoder().decode(bytes.subarray(off, off + len));

async function inflateRaw(bytes) {
  const ds = new DecompressionStream('deflate-raw');
  const stream = new Blob([bytes]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

const IMG_EXT = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' };

function b64FromBytes(bytes) {
  let out = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    out += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(out);
}

/** N-122: the pictures inside a docx (word/media/*) - a TDS's nutrition table is often one.
 *  Returns up to 3 images as base64; anything larger than ~1.5MB encoded is skipped. */
async function docxMedia(buf) {
  const b = new Uint8Array(buf);
  const dv = new DataView(buf);
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65558); i -= 1) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) return [];
  const count = dv.getUint16(eocd + 10, true);
  let off = dv.getUint32(eocd + 16, true);
  const names = [];
  for (let n = 0; n < count; n += 1) {
    if (dv.getUint32(off, true) !== 0x02014b50) break;
    const nameLen = dv.getUint16(off + 28, true);
    const extraLen = dv.getUint16(off + 30, true);
    const cmtLen = dv.getUint16(off + 32, true);
    const name = dosName(b, off + 46, nameLen);
    const ext = (name.match(/\.([a-z0-9]+)$/i) || [])[1];
    if (/^word\/media\//i.test(name) && ext && IMG_EXT[ext.toLowerCase()]) {
      names.push({ name, mime: IMG_EXT[ext.toLowerCase()] });
    }
    off += 46 + nameLen + extraLen + cmtLen;
  }
  const out = [];
  for (const { name, mime } of names.slice(0, 3)) {
    try {
      const bytes = await unzipEntry(buf, name);
      const data_b64 = b64FromBytes(bytes);
      if (data_b64.length > 100 && data_b64.length <= 1_500_000) out.push({ media_type: mime, data_b64 });
    } catch { /* an unreadable image is skipped, not fatal */ }
  }
  return out;
}

/** Minimal zip READER (store + deflate entries), the twin of zipStore.js's writer. */
async function unzipEntry(buf, wantedName) {
  const b = new Uint8Array(buf);
  const dv = new DataView(buf);
  // find End Of Central Directory (no comment case first, then scan back)
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65558); i -= 1) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('file-not-zip');
  const count = dv.getUint16(eocd + 10, true);
  let off = dv.getUint32(eocd + 16, true);
  for (let n = 0; n < count; n += 1) {
    if (dv.getUint32(off, true) !== 0x02014b50) break;
    const method = dv.getUint16(off + 10, true);
    const csize = dv.getUint32(off + 20, true);
    const nameLen = dv.getUint16(off + 28, true);
    const extraLen = dv.getUint16(off + 30, true);
    const cmtLen = dv.getUint16(off + 32, true);
    const lho = dv.getUint32(off + 42, true);
    const name = dosName(b, off + 46, nameLen);
    if (name === wantedName) {
      const lhNameLen = dv.getUint16(lho + 26, true);
      const lhExtraLen = dv.getUint16(lho + 28, true);
      const dataStart = lho + 30 + lhNameLen + lhExtraLen;
      const comp = b.subarray(dataStart, dataStart + csize);
      if (method === 0) return comp;
      if (method === 8) return await inflateRaw(comp);
      throw new Error('file-unsupported');
    }
    off += 46 + nameLen + extraLen + cmtLen;
  }
  throw new Error('file-unsupported');
}

function docxXmlToText(xml) {
  return xml
    .replace(/<w:p[ >]/g, '\n<w:p ')
    .replace(/<w:tab\/>/g, '\t')
    .replace(/<w:br\/>/g, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n')
    .trim();
}

async function xlsxToText(buf) {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(buf, { type: 'array' });
  const lines = [];
  for (const name of wb.SheetNames) {
    lines.push(`## ${name}`);
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: false, defval: '' });
    for (const row of rows) {
      const line = row.map((c) => String(c).trim()).join(' | ').replace(/(\s\|\s)+$/, '');
      if (line.trim()) lines.push(line);
    }
  }
  return lines.join('\n');
}

async function pdfToText(buf) {
  const pdfjs = await import('pdfjs-dist/build/pdf');
  const worker = await import('pdfjs-dist/build/pdf.worker.entry');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;
  const doc = await pdfjs.getDocument({ data: buf }).promise;
  const pages = [];
  for (let i = 1; i <= doc.numPages; i += 1) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    pages.push(content.items.map((it) => it.str).join(' '));
  }
  return pages.join('\n\n').replace(/[ \t]{2,}/g, ' ').trim();
}

/**
 * @returns {Promise<string>} extracted plain text
 * @throws Error with message 'file-no-text' | 'file-unsupported' | 'file-not-zip'
 */
export async function extractFileText(file) {
  const name = (file.name || '').toLowerCase();
  let text;
  // N-122: docx pictures are harvested alongside the text - the caller sends them to a vision
  // model instead of silently losing whatever the pictures say (a TDS's NFT is often one).
  let images = [];
  if (/\.(txt|md|markdown|csv)$/.test(name)) {
    text = await file.text();
  } else if (/\.docx$/.test(name)) {
    const buf = await file.arrayBuffer();
    const xmlBytes = await unzipEntry(buf, 'word/document.xml');
    text = docxXmlToText(new TextDecoder().decode(xmlBytes));
    images = await docxMedia(buf);
  } else if (/\.(xlsx|xls)$/.test(name)) {
    text = await xlsxToText(await file.arrayBuffer());
  } else if (/\.pdf$/.test(name)) {
    text = await pdfToText(await file.arrayBuffer());
  } else {
    // .doc (the pre-2007 binary format) lands here on purpose: refusing with a clear name beats
    // decoding garbage into an intake.
    throw new Error('file-unsupported');
  }
  if (!text || text.replace(/\s+/g, '').length < 40) throw new Error('file-no-text');
  return { text, images };
}

export const ACCEPT_DOCS = '.txt,.md,.docx,.pdf,.xlsx,.xls';
